import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { CheckCircle2 } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { formatMXN } from '../lib/utils';
import { trackWhatsAppClick } from '../lib/analytics';
import { BACKEND_URL } from '../lib/config';
import { products, waLinkProps, WHATSAPP_NUMBER } from '../mock';
import SiteHeader from './SiteHeader';
import AboutContact from './AboutContact';
import WhatsAppIcon from './WhatsAppIcon';

// Los pedidos pagados llegan al mismo WhatsApp del dueño que usa el resto del sitio
// (WHATSAPP_NUMBER en mock.js). Ya no se lee de REACT_APP_SHIPPING_WHATSAPP: ese .env local
// tenía el número de pruebas del colaborador.
const SHIPPING_WHATSAPP = WHATSAPP_NUMBER;

// Nombre del sitio para cada id que trae el pago verificado (las variantes llevan modelo +
// color, igual que en el carrito). Los ids salen de Mercado Pago, no del navegador.
const ITEM_NAMES = products.reduce(
  (names, p) => {
    names[p.id] = p.name;
    (p.variants || []).forEach((v) => {
      names[v.id] = `${p.name} — ${v.name}`;
    });
    return names;
  },
  { envio: 'Envío' }
);

const readLastOrderRef = () => {
  try {
    return JSON.parse(localStorage.getItem('quinlate_last_order') || 'null')?.ref || null;
  } catch {
    return null;
  }
};

// El estado, el monto y las piezas los da el servidor, que consulta el pago en Mercado Pago:
// la URL (`?status=approved`) y el navegador se pueden falsificar. Render (plan gratis)
// puede tardar en despertar, así que se reintenta antes de rendirse.
async function fetchVerifiedPayment(paymentId, ref) {
  const query = new URLSearchParams({ payment_id: paymentId });
  if (ref) query.set('ref', ref);
  for (let attempt = 1; attempt <= 3; attempt += 1) {
    try {
      const res = await fetch(`${BACKEND_URL}/api/orders/verify?${query}`);
      if (res.ok) return { payment: await res.json() };
      if (res.status === 400 || res.status === 404) return { notFound: true };
    } catch {
      // red caída o servidor despertando: se reintenta
    }
    if (attempt < 3) await new Promise((resolve) => setTimeout(resolve, 2500 * attempt));
  }
  return { error: true };
}

// Estado verificado de Mercado Pago -> pantalla. Pendiente = ficha de OXXO/efectivo o SPEI
// sin pagar, o tarjeta en revisión: el pedido existe, así que también se piden los datos de
// envío. Cancelado, reembolsado o contracargo se tratan como rechazado.
const viewForStatus = (status) => {
  if (status === 'approved') return 'approved';
  if (['pending', 'in_process', 'authorized'].includes(status)) return 'pending';
  return 'rejected';
};

// Título de la pestaña según el estado verificado.
const TAB_TITLES = {
  loading: 'Confirmando tu pago — QUINLATE',
  approved: 'Pago confirmado — QUINLATE',
  pending: 'Pago pendiente — QUINLATE',
  in_review: 'Pago en revisión — QUINLATE',
  rejected: 'Pago no aprobado — QUINLATE',
  none: 'Regresaste sin pagar — QUINLATE',
  not_found: 'Pago no encontrado — QUINLATE',
  error: 'No pudimos confirmar tu pago — QUINLATE',
};

// Pantallas sin formulario (cargando, sin pago, rechazado, error...).
const MessageView = ({ onOpenProduct, testid, eyebrow, title, text, children }) => (
  <>
    <SiteHeader onOpenProduct={onOpenProduct} />
    <section className="q-section" data-testid={testid}>
      <div className="q-section__inner">
        <div className="q-section__head">
          <span className="q-section__eyebrow">{eyebrow}</span>
          <h2 className="q-section__title">{title}</h2>
          {text && <p className="q-section__sub">{text}</p>}
        </div>
        {children && <div className="q-cart__empty-links">{children}</div>}
      </div>
    </section>
    <AboutContact />
  </>
);

// 10 dígitos. El autollenado del celular a veces trae la lada de país ("+52 33 …"): se quita.
const phoneDigits = (value) => {
  const digits = value.replace(/\D/g, '');
  return digits.length === 12 && digits.startsWith('52') ? digits.slice(2) : digits;
};

const EMPTY_FORM = {
  name: '',
  company: '',
  email: '',
  phone: '',
  street: '',
  ext_number: '',
  int_number: '',
  neighborhood: '',
  zip_code: '',
  municipality: '',
  state: '',
  references: '',
};

const ConfirmationPage = ({ onOpenProduct }) => {
  const location = useLocation();
  const { clear } = useCart();

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  // Mercado Pago manda el número del pago en la URL ("null" si el cliente volvió sin pagar).
  // Es solo la llave para preguntarle al servidor; nada más se lee de la URL.
  const rawPaymentId = params.get('payment_id') || params.get('collection_id');
  const paymentId = rawPaymentId && /^\d+$/.test(rawPaymentId) ? rawPaymentId : null;

  // loading | approved | pending | rejected | none | not_found | error
  const [view, setView] = useState('loading');
  const [payment, setPayment] = useState(null);
  const [retry, setRetry] = useState(0);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  const [waUrl, setWaUrl] = useState(null);

  useEffect(() => {
    if (!paymentId) {
      setView('none');
      return undefined;
    }
    let cancelled = false;
    setView('loading');
    fetchVerifiedPayment(paymentId, readLastOrderRef()).then((result) => {
      if (cancelled) return;
      if (result.payment) {
        setPayment(result.payment);
        setView(viewForStatus(result.payment.status));
      } else {
        setView(result.notFound ? 'not_found' : 'error');
      }
    });
    return () => {
      cancelled = true;
    };
  }, [paymentId, retry]);

  useEffect(() => {
    // Con pago aprobado o pendiente el pedido ya existe: se vacía el carrito para que no se
    // pague dos veces. Solo si es el pedido que salió de este navegador; si no, el carrito
    // guardado puede ser otra compra.
    if ((view === 'approved' || view === 'pending') && payment?.ref_matches === true) clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, payment]);

  const items = payment?.items || [];
  const total = payment?.amount ?? 0;
  const isPending = view === 'pending';
  const inReview = payment?.status === 'in_process';

  useEffect(() => {
    document.title = TAB_TITLES[isPending && inReview ? 'in_review' : view] || TAB_TITLES.loading;
  }, [view, isPending, inReview]);

  const validate = () => {
    const next = {};
    if (form.name.trim().length < 2) next.name = 'Escribe tu nombre completo.';
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) next.email = 'Correo inválido.';
    if (phoneDigits(form.phone).length !== 10) next.phone = 'Debe tener 10 dígitos.';
    if (form.street.trim().length < 3) next.street = 'Falta la calle.';
    if (form.ext_number.trim().length < 1) next.ext_number = 'Falta el número exterior.';
    if (form.neighborhood.trim().length < 2) next.neighborhood = 'Falta la colonia.';
    if (!/^\d{5}$/.test(form.zip_code.trim())) next.zip_code = 'Código postal inválido (5 dígitos).';
    if (form.municipality.trim().length < 2) next.municipality = 'Falta el municipio.';
    if (form.state.trim().length < 2) next.state = 'Falta el estado.';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  // Todo lo del pedido (piezas, estado, monto, número de pago) sale del pago verificado.
  const buildWaMessage = () => {
    const lines = items
      .map((i) => `- ${ITEM_NAMES[i.id] || 'Pieza QUINLATE'} x${i.quantity ?? 1}`)
      .join('\n');
    const header = isPending
      ? `Pedido con PAGO PENDIENTE: todavía no se acredita, no enviar hasta que Mercado Pago lo marque como Aprobado (pago #${payment.payment_id}, ref ${payment.external_reference}):`
      : `Pedido PAGADO, verificado con Mercado Pago (pago #${payment.payment_id}, ref ${payment.external_reference}):`;
    return (
      `${header}\n${lines}\n` +
      `${isPending ? 'Total a pagar' : 'Total pagado'}: $${formatMXN(total)} MXN\n\n` +
      `Datos de envío:\n` +
      `${form.name}${form.company ? ` (${form.company})` : ''}\n${form.email}\n${form.phone}\n` +
      `${form.street} ${form.ext_number}${form.int_number ? ` int. ${form.int_number}` : ''}, ${form.neighborhood}\n` +
      `C.P. ${form.zip_code}, ${form.municipality}, ${form.state}` +
      (form.references ? `\nReferencias: ${form.references}` : '')
    );
  };

  const handleChange = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    // El click a WhatsApp va PRIMERO y de forma síncrona (nada de `await` antes) — un
    // <a> con .click() real es lo único que Safari/Chrome en celular respetan de forma
    // confiable como resultado directo del toque del usuario. Cualquier `window.open`
    // o navegación hecha después de esperar una petición (aunque sea un `await` corto)
    // se trata como pop-up no solicitado y se bloquea en silencio — así es como ya
    // funcionan los otros botones de WhatsApp del sitio (enlaces <a>, no JS diferido).
    if (SHIPPING_WHATSAPP) {
      const url = `https://wa.me/${SHIPPING_WHATSAPP}?text=${encodeURIComponent(buildWaMessage())}`;
      trackWhatsAppClick('confirmation_page', { paymentId: payment.payment_id });
      setWaUrl(url);
      const link = document.createElement('a');
      link.href = url;
      if (waLinkProps.target) {
        link.target = waLinkProps.target;
        link.rel = waLinkProps.rel;
      }
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
    setSubmitted(true);

    // El guardado en la base de datos va en segundo plano, sin bloquear nada de lo de
    // arriba — si falla o tarda, no importa: el mensaje de WhatsApp ya salió con todos
    // los datos.
    setSubmitting(true);
    fetch(`${BACKEND_URL}/api/orders/shipping`, {
      method: 'POST',
      // keepalive: en celular WhatsApp abre en la misma pestaña; sin esto el navegador
      // podría cancelar el guardado al salir de la página.
      keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        payment_id: payment.payment_id,
        name: form.name.trim(),
        company: form.company.trim() || null,
        email: form.email.trim(),
        phone: phoneDigits(form.phone),
        street: form.street.trim(),
        ext_number: form.ext_number.trim(),
        int_number: form.int_number.trim() || null,
        neighborhood: form.neighborhood.trim(),
        zip_code: form.zip_code.trim(),
        municipality: form.municipality.trim(),
        state: form.state.trim(),
        references: form.references.trim() || null,
      }),
    })
      .then((res) => res.json().catch(() => ({})).then((data) => ({ ok: res.ok, data })))
      .then(({ ok, data }) => setSaveFailed(!ok || !data.saved))
      .catch(() => setSaveFailed(true))
      .finally(() => setSubmitting(false));
  };

  const cartLink = (
    <Link to="/carrito" className="q-btn q-btn--gold q-btn--sm">
      Volver al carrito
    </Link>
  );
  const whatsAppHelp = (source) => (
    <a
      href={`https://wa.me/${SHIPPING_WHATSAPP}?text=${encodeURIComponent(`Hola QUINLATE, pagué con Mercado Pago (pago #${paymentId}) y no pude confirmar mi pedido en la página.`)}`}
      {...waLinkProps}
      className="q-btn q-btn--ghost q-btn--sm q-btn--wa"
      onClick={() => trackWhatsAppClick(source, { paymentId })}
    >
      <WhatsAppIcon size={16} /> Escribir por WhatsApp
    </a>
  );

  if (view === 'loading') {
    return (
      <MessageView
        onOpenProduct={onOpenProduct}
        testid="confirmation-loading"
        eyebrow="Pago"
        title="Confirmando tu pago…"
        text="Estamos verificándolo con Mercado Pago, tarda unos segundos."
      />
    );
  }

  if (view === 'none') {
    return (
      <MessageView
        onOpenProduct={onOpenProduct}
        testid="confirmation-none"
        eyebrow="Pago"
        title="Regresaste sin pagar"
        text="No se hizo ningún cargo. Tu carrito sigue guardado para cuando quieras terminar tu compra."
      >
        {cartLink}
      </MessageView>
    );
  }

  if (view === 'rejected') {
    return (
      <MessageView
        onOpenProduct={onOpenProduct}
        testid="confirmation-rejected"
        eyebrow="Pago"
        title="No se aprobó tu pago"
        text="Mercado Pago no aprobó el cobro. Tu carrito sigue guardado: intenta con otro método de pago o con otra tarjeta."
      >
        {cartLink}
      </MessageView>
    );
  }

  if (view === 'not_found') {
    return (
      <MessageView
        onOpenProduct={onOpenProduct}
        testid="confirmation-not-found"
        eyebrow="Pago"
        title="No encontramos este pago"
        text={`Si ya pagaste, escríbenos por WhatsApp con tu número de pago #${paymentId} y lo revisamos.`}
      >
        {whatsAppHelp('confirmation_not_found')}
        {cartLink}
      </MessageView>
    );
  }

  if (view === 'error') {
    return (
      <MessageView
        onOpenProduct={onOpenProduct}
        testid="confirmation-error"
        eyebrow="Pago"
        title="No pudimos confirmar tu pago"
        text={`Nuestro sistema no respondió. Si ya pagaste, tu pago está seguro en Mercado Pago: escríbenos por WhatsApp con tu número de pago #${paymentId} y lo revisamos.`}
      >
        <button type="button" className="q-btn q-btn--gold q-btn--sm" onClick={() => setRetry((r) => r + 1)}>
          Intentar de nuevo
        </button>
        {whatsAppHelp('confirmation_error')}
      </MessageView>
    );
  }

  // approved o pending: el pedido existe, se muestra lo verificado y se piden los datos de envío.
  const heading = !isPending
    ? {
        eyebrow: 'Pago confirmado',
        title: '¡Gracias por tu compra!',
        text: 'Déjanos tus datos de envío para coordinar la entrega.',
      }
    : inReview
      ? {
          eyebrow: 'Pago en revisión',
          title: 'Tu pago está en revisión',
          text: 'Mercado Pago lo está revisando; normalmente se resuelve en minutos. Déjanos tus datos de envío y enviamos tu pedido en cuanto se apruebe.',
        }
      : {
          eyebrow: 'Pago pendiente',
          title: 'Tu ficha está lista',
          text: 'Págala como te indicó Mercado Pago y enviamos tu pedido en cuanto se acredite. Déjanos tus datos de envío para tenerlo listo.',
        };

  return (
    <>
      <SiteHeader onOpenProduct={onOpenProduct} />
      <section className="q-section" data-testid={isPending ? 'confirmation-pending' : 'confirmation-approved'}>
        <div className="q-section__inner">
          <div className="q-section__head">
            <span className="q-section__eyebrow">{heading.eyebrow}</span>
            <h2 className="q-section__title">{heading.title}</h2>
            <p className="q-section__sub">{heading.text}</p>
          </div>

          {items.length > 0 && (
            <ul className="q-cart__list" data-testid="confirmation-order-summary">
              {items.map((i) => (
                <li key={i.id} className="q-cart__item">
                  <div className="q-cart__item-info">
                    <span className="q-cart__item-name">{ITEM_NAMES[i.id] || 'Pieza QUINLATE'}</span>
                    <span className="q-cart__item-meta">
                      Cantidad: {i.quantity ?? 1}
                      {i.unit_price != null ? ` · $${formatMXN(i.unit_price)} MXN` : ''}
                    </span>
                  </div>
                </li>
              ))}
              <div className="q-cart__total">
                <span>{isPending ? 'Total a pagar' : 'Total pagado'}</span>
                <strong>${formatMXN(total)} MXN</strong>
              </div>
            </ul>
          )}

          {submitted ? (
            <div className="q-empty" data-testid="confirmation-submitted">
              <CheckCircle2 size={28} strokeWidth={1.5} color="var(--q-gold-fill)" />
              <p className="q-empty__title">¡Listo!</p>
              <p className="q-empty__text">
                {isPending
                  ? 'En cuanto se acredite tu pago te contactamos para coordinar la entrega.'
                  : 'Te vamos a contactar para coordinar la entrega.'}
                {saveFailed && ' (Tus datos no se pudieron guardar, pero van completos en el WhatsApp.)'}
              </p>
              {waUrl && (
                <a
                  href={waUrl}
                  {...waLinkProps}
                  className="q-btn q-btn--ghost q-btn--sm"
                  data-testid="confirmation-wa-fallback"
                  style={{ marginTop: 16 }}
                >
                  <WhatsAppIcon size={16} /> Si no se abrió WhatsApp, toca aquí
                </a>
              )}
            </div>
          ) : (
            <form className="q-shipping-form" onSubmit={handleSubmit} data-testid="shipping-form">
              <div className="q-shipping-form__row">
                <label className="q-shipping-form__field">
                  <span>Nombre completo</span>
                  <input
                    name="name"
                    autoComplete="name"
                    value={form.name}
                    onChange={handleChange('name')}
                    data-testid="shipping-name"
                  />
                  {errors.name && <em>{errors.name}</em>}
                </label>
                <label className="q-shipping-form__field">
                  <span>Empresa (opcional)</span>
                  <input
                    name="organization"
                    autoComplete="organization"
                    value={form.company}
                    onChange={handleChange('company')}
                    data-testid="shipping-company"
                  />
                </label>
              </div>

              <div className="q-shipping-form__row">
                <label className="q-shipping-form__field">
                  <span>Correo</span>
                  <input
                    type="email"
                    name="email"
                    autoComplete="email"
                    value={form.email}
                    onChange={handleChange('email')}
                    data-testid="shipping-email"
                  />
                  {errors.email && <em>{errors.email}</em>}
                </label>
                <label className="q-shipping-form__field">
                  <span>Teléfono (10 dígitos)</span>
                  <input
                    type="tel"
                    name="tel"
                    autoComplete="tel-national"
                    inputMode="numeric"
                    value={form.phone}
                    onChange={handleChange('phone')}
                    data-testid="shipping-phone"
                  />
                  {errors.phone && <em>{errors.phone}</em>}
                </label>
              </div>

              <label className="q-shipping-form__field">
                <span>Calle</span>
                <input
                  name="address-line1"
                  autoComplete="address-line1"
                  value={form.street}
                  onChange={handleChange('street')}
                  data-testid="shipping-street"
                />
                {errors.street && <em>{errors.street}</em>}
              </label>

              <div className="q-shipping-form__row">
                <label className="q-shipping-form__field">
                  <span>No. exterior</span>
                  <input
                    name="ext_number"
                    value={form.ext_number}
                    onChange={handleChange('ext_number')}
                    data-testid="shipping-ext-number"
                  />
                  {errors.ext_number && <em>{errors.ext_number}</em>}
                </label>
                <label className="q-shipping-form__field">
                  <span>No. interior (opcional)</span>
                  <input
                    name="address-line2"
                    autoComplete="address-line2"
                    value={form.int_number}
                    onChange={handleChange('int_number')}
                    data-testid="shipping-int-number"
                  />
                </label>
              </div>

              <div className="q-shipping-form__row">
                <label className="q-shipping-form__field">
                  <span>Código postal</span>
                  <input
                    name="postal-code"
                    autoComplete="postal-code"
                    inputMode="numeric"
                    maxLength={5}
                    value={form.zip_code}
                    onChange={handleChange('zip_code')}
                    data-testid="shipping-zip"
                  />
                  {errors.zip_code && <em>{errors.zip_code}</em>}
                </label>
                <label className="q-shipping-form__field">
                  <span>Colonia</span>
                  <input
                    name="address-level3"
                    autoComplete="address-level3"
                    value={form.neighborhood}
                    onChange={handleChange('neighborhood')}
                    data-testid="shipping-neighborhood"
                  />
                  {errors.neighborhood && <em>{errors.neighborhood}</em>}
                </label>
              </div>

              <div className="q-shipping-form__row">
                <label className="q-shipping-form__field">
                  <span>Municipio</span>
                  <input
                    name="address-level2"
                    autoComplete="address-level2"
                    value={form.municipality}
                    onChange={handleChange('municipality')}
                    data-testid="shipping-municipality"
                  />
                  {errors.municipality && <em>{errors.municipality}</em>}
                </label>
                <label className="q-shipping-form__field">
                  <span>Estado</span>
                  <input
                    name="address-level1"
                    autoComplete="address-level1"
                    value={form.state}
                    onChange={handleChange('state')}
                    data-testid="shipping-state"
                  />
                  {errors.state && <em>{errors.state}</em>}
                </label>
              </div>

              <label className="q-shipping-form__field">
                <span>Referencias (opcional)</span>
                <input
                  name="references"
                  autoComplete="off"
                  value={form.references}
                  onChange={handleChange('references')}
                  data-testid="shipping-references"
                />
              </label>

              <button
                type="submit"
                className="q-btn q-btn--gold"
                disabled={submitting}
                data-testid="shipping-submit"
              >
                <WhatsAppIcon size={16} />{' '}
                {submitting ? 'Enviando…' : 'Enviar y avisar por WhatsApp'}
              </button>
            </form>
          )}
        </div>
      </section>
      <AboutContact />
    </>
  );
};

export default ConfirmationPage;
